// filepath: apps/web/src/tenant/TenantProvider.tsx
//
// TenantProvider supplies tenantSlug via React context. tenantSlug is
// REQUIRED in every TanStack Query cache key that touches a tenant-scoped
// resource (see ARCH Rule 1, INV-1).
//
// Production wiring (FR-UI-CORE-1): tenantSlug is resolved from the
// subdomain via tenantSlugFromHostname(window.location.hostname), e.g.
// "acme.platform.example" → "acme". localhost/127.0.0.1 and bare hostnames
// (no dots) fall back to "local".
//
// SECURITY: this provider never holds OIDC tokens or other credentials.
// Token storage is memory-only (ARCHITECTURE_WEB.md §7, INV-token-storage).

import React from "react";

/**
 * Extracts the tenant slug from a hostname by returning the first
 * subdomain label (everything before the first dot).
 *
 * Examples:
 *   "acme.ascoa.io"    → "acme"
 *   "demo.localhost"   → "demo"
 *   "localhost"        → "local"
 *   "127.0.0.1"        → "local"
 *   ""                 → "local"
 *
 * This is a pure string transformation — zero network I/O.
 * It implements step 1 of ARCHITECTURE_WEB.md §3 bootstrap sequence.
 * Steps 2–4 (realm URL fetch + OIDC init) are FR-UI-CORE-2's domain.
 */
export function tenantSlugFromHostname(hostname: string): string {
  if (!hostname) return "local";
  const dotIndex = hostname.indexOf(".");
  if (dotIndex <= 0) return "local";
  const firstLabel = hostname.slice(0, dotIndex);
  // IPv4 addresses (e.g. 127.0.0.1): first label is all digits → local fallback.
  if (/^\d+$/.test(firstLabel)) return "local";
  return firstLabel;
}

export interface TenantContextValue {
  tenantSlug: string;
}

const TenantContext = React.createContext<TenantContextValue | null>(null);

export interface TenantProviderProps {
  tenantSlug: string;
  children: React.ReactNode;
}

export function TenantProvider({
  tenantSlug,
  children,
}: TenantProviderProps): React.ReactElement {
  // Default to "local" so tests and offline dev work without bootstrap.
  const value: TenantContextValue = React.useMemo(
    () => ({ tenantSlug: tenantSlug || "local" }),
    [tenantSlug],
  );
  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const ctx = React.useContext(TenantContext);
  if (ctx === null) {
    // Fallback for components rendered outside of <TenantProvider>
    // (e.g. unit-tested in isolation). Tenant slug defaults to "local".
    return { tenantSlug: "local" };
  }
  return ctx;
}
